import React, { useContext, useState } from "react";
import dayjs from "dayjs";
import isBetweenPlugin from "dayjs/plugin/isBetween";
import utcPlugin from "dayjs/plugin/utc";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar, PickersDay } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { styled } from "@mui/material/styles";
import Popover from "@mui/material/Popover";
import Box from "@mui/material/Box";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AppContext from "../../config/AppContext";
import CloseIcon from '@mui/icons-material/Close';
import "dayjs/locale/en";
import "dayjs/locale/zh";
import "dayjs/locale/es";
import "dayjs/locale/ja";
import "dayjs/locale/ko";
import { useTranslation } from "react-i18next";

dayjs.extend(isBetweenPlugin);
dayjs.extend(utcPlugin);

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) =>
    prop !== "isRangeStart" && prop !== "isRangeEnd" && prop !== "isInRange",
})(({ isRangeStart, isRangeEnd, isInRange, mode }) => ({
  borderRadius: 0,
  padding: '20px !important',

  ...(isInRange && {
    borderRadius: "6px",
    backgroundColor: "#FFE0B2",
    color: "#242424",
  }),
  ...(isRangeStart && {
    borderRadius: "6px",
    background: "transparent linear-gradient(315deg, #FA9C2B 0%, #C1700E 100%) 0% 0% no-repeat padding-box",
    color: "#fff",
    fontWeight: "bold",
  }),
  ...(isRangeEnd && {
    borderRadius: "6px",
    background: "transparent linear-gradient(315deg, #FA9C2B 0%, #C1700E 100%) 0% 0% no-repeat padding-box",
    color: "#fff",
    fontWeight: "bold",
  }),
}));

function CustomizedDateRangePicker({ startDate = null, setStartDate = () => { }, endDate = null, setEndDate = () => { }, ...props }) {

  const [anchorEl, setAnchorEl] = useState(null);
  const { mode } = useContext(AppContext);
  const { t, i18n } = useTranslation();
  const dayLabels = [
    t("RAL0765"),
    t("RAL0766"),
    t("RAL0767"),
    t("RAL0768"),
    t("RAL0769"),
    t("RAL0770"),
    t("RAL0771"),
  ];

  React.useEffect(() => {
    dayjs.locale(i18n.language || "en");
  }, [i18n.language]);

  const openPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const closePopover = () => {
    setAnchorEl(null);
  };

  const handleDaySelect = (day) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(day);
      setEndDate(null);
    } else if (day.isBefore(startDate, "day")) {
      setEndDate(startDate);
      setStartDate(day);
      closePopover();
    } else {
      setEndDate(day);
      closePopover();
    }
  };

  const isWithinRange = (day) => {
    if (!startDate) return false;
    if (!endDate) return day.isSame(startDate, "day");
    return day.isBetween(startDate, endDate, "day", "[]");
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Box
        onClick={openPopover}
        sx={{
          padding: "13px 14px 13px 14px",
          borderRadius: "5px",
          cursor: "pointer",
          userSelect: "none",
          minWidth: "100%",
          color: mode === "dark" ? "#f8f8f8cc" : "#242424",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: mode === "dark" ? "#01010180" : "#f4f5f8"
        }}
        tabIndex={0}
      >
        <Box>
          {startDate && endDate
            ? `${startDate.format("DD MMM YYYY")} - ${endDate.format("DD MMM YYYY")
            }`
            : t("RAL1161")}
        </Box>
        {startDate && endDate && (
          <CloseIcon sx={{ ml: 1 }} fontSize="small"
            onClick={(e) => {
              e.stopPropagation();
              setStartDate(null);
              setEndDate(null);
              closePopover()
            }}
          />)}
        <CalendarTodayIcon fontSize="small" sx={{color: mode === "dark" ? "#f8f8f8" : "#a2a4a6", opacity:mode === "dark" && "0.5"}} />
      </Box>

      <Popover
        id="date-range-popover"
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        disableRestoreFocus
        sx={{ ml: -43.5, mt: -15 }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
          <DateCalendar
            value={startDate || dayjs()}
            onChange={handleDaySelect}
            sx={{
              "& .MuiDayCalendar-weekDayLabel":{
                padding: "20px !important",
              }
            }}
            slots={{
              day: (dayProps) => {
                const day = dayProps.day;
                const isRangeStart = startDate && day.isSame(startDate, "day");
                const isRangeEnd = endDate && day.isSame(endDate, "day");
                const isInRange = isWithinRange(day) && !isRangeStart && !isRangeEnd;
                return (
                  <CustomPickersDay
                    {...dayProps}
                    day={day}
                    isRangeStart={isRangeStart}
                    isRangeEnd={isRangeEnd}
                    isInRange={isInRange}
                    mode={mode}
                  />
                );
              },
            }}
          />
        </LocalizationProvider>
      </Popover>
    </>
  );
}

export default CustomizedDateRangePicker;
